"use client"

import { useState, useRef } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion, useInView } from "motion/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Calendar,
  Users,
  Palette,
  Music,
  Trophy,
  Dumbbell,
  GraduationCapIcon,
} from "lucide-react"

export default function ProgramsPage() {
  const [activeTab, setActiveTab] = useState("early-childhood")
  const featuredRef = useRef(null)
  const isFeaturedInView = useInView(featuredRef, { once: true, amount: 0.2 })


  return (
    <div className="min-h-screen flex flex-col">
        <section className="relative bg-muted py-8 md:py-10">
          <div className="container mx-auto px-4">
            <div className="mb-6">
              <Link
                href="/"
                className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Link>
            </div>
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="text-4xl font-bold tracking-tight mb-4">Our Academic Programs</h1>
                <p className="text-muted-foreground mb-6">
                  At Shree Janakalyan Boarding School, we offer comprehensive educational programs designed to nurture
                  young minds and develop well-rounded individuals. Our curriculum balances academic excellence with
                  character development, cultural awareness, and practical skills.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild>
                    <a href="#programs">Explore Programs</a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="#curriculum">View Curriculum</a>
                  </Button>
                </div>
              </motion.div>
              <motion.div
                className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Image
                  src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742036718/teaching_d3cw2g.jpg"
                  alt="Students in classroom"
                  fill
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                  <p className="text-white text-lg font-medium">Nurturing young minds through quality education</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Educational Philosophy Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              className="mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">Our Educational Philosophy</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We believe in providing a holistic education that nurtures intellectual, physical, emotional, and social
                development.
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  title: "Child-Centered Learning",
                  description:
                    "We recognize that each child is unique with different abilities, interests, and learning styles. Our teaching approaches are tailored to meet individual needs while fostering collaboration and community.",
                  icon: <Users className="h-8 w-8 text-primary" />,
                },
                {
                  title: "Holistic Development",
                  description:
                    "Beyond academics, we focus on developing character, creativity, critical thinking, and social skills. Our programs integrate arts, sports, cultural activities, and community service.",
                  icon: <Palette className="h-8 w-8 text-primary" />,
                },
                {
                  title: "Cultural Roots & Global Perspective",
                  description:
                    "We take pride in our Nepali heritage while preparing students for a global future. Students learn to respect their cultural roots while developing an understanding of diverse perspectives.",
                  icon: <GraduationCapIcon className="h-8 w-8 text-primary" />,
                },
              ].map((philosophy, index) => (
                <motion.div
                  key={philosophy.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        {philosophy.icon}
                      </div>
                      <CardTitle>{philosophy.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{philosophy.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Programs Overview Section */}
        <section id="programs" className="py-16 md:py-24 bg-muted">
          <div className="container mx-auto px-4">
            <motion.div
              className="mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">Our Programs</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive education for children aged 3-14, structured to meet developmental needs at each stage.
              </p>
            </motion.div>

            <Tabs defaultValue="early-childhood" className="space-y-8" onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-1 md:grid-cols-3">
                <TabsTrigger value="early-childhood">Early Childhood (Preschool - Grade 1)</TabsTrigger>
                <TabsTrigger value="middle-childhood">Middle Childhood (Grades 2-5)</TabsTrigger>
                <TabsTrigger value="upper-primary">Upper Primary (Grades 6-8)</TabsTrigger>
              </TabsList>

              <TabsContent value="early-childhood" className="space-y-4">
                <div className="grid gap-8 md:grid-cols-2 items-center">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={activeTab === "early-childhood" ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-2xl font-bold mb-4">Early Childhood Program</h3>
                    <p className="text-muted-foreground mb-4">
                      Our Early Childhood program focuses on building strong foundations in literacy, numeracy, and
                      social skills through play-based learning. Children at this stage are naturally curious, and we
                      nurture this curiosity through engaging, hands-on activities.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2">
                        <BookOpen className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Foundational Skills:</span> Focus on reading, writing, and basic
                          mathematics through interactive methods
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Social Development:</span> Learning to share, cooperate, and
                          build friendships
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Palette className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Creative Expression:</span> Art, music, and storytelling to
                          develop imagination and creativity
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Daily Schedule:</span> Balanced routine with alternating active
                          and quiet activities
                        </span>
                      </li>
                    </ul>
                    <Button asChild>
                      <Link href="/online-admission">Apply for Early Childhood</Link>
                    </Button>
                  </motion.div>
                  <motion.div
                    className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
                    initial={{ opacity: 0, x: 50 }}
                    animate={activeTab === "early-childhood" ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Image
                      src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742039213/prenmam_caznnv.jpg"
                      alt="Early childhood classroom"
                      fill
                      className="object-cover"
                    />
                  </motion.div>
                </div>
              </TabsContent>

              <TabsContent value="middle-childhood" className="space-y-4">
                <div className="grid gap-8 md:grid-cols-2 items-center">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={activeTab === "middle-childhood" ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-2xl font-bold mb-4">Middle Childhood Program</h3>
                    <p className="text-muted-foreground mb-4">
                      In Grades 2-5, we expand on foundational knowledge with the introduction of more complex concepts
                      in science, social studies, and cultural education. Students begin to develop critical thinking
                      skills and greater independence in their learning.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2">
                        <BookOpen className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Advanced Literacy:</span> Reading comprehension, writing
                          composition, and vocabulary expansion
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Mathematical Concepts:</span> Problem-solving, patterns, and
                          basic operations
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Palette className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Science & Social Studies:</span> Introduction to scientific
                          inquiry and understanding of community and culture
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Project-Based Learning:</span> Collaborative projects that
                          integrate multiple subjects
                        </span>
                      </li>
                    </ul>
                    <Button asChild>
                      <Link href="/online-admission">Apply for Middle Childhood</Link>
                    </Button>
                  </motion.div>
                  <motion.div
                    className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
                    initial={{ opacity: 0, x: 50 }}
                    animate={activeTab === "middle-childhood" ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Image
                      src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742039212/2class_xnkvru.jpg"
                      alt="Middle childhood classroom"
                      fill
                      className="object-cover"
                    />
                  </motion.div>
                </div>
              </TabsContent>

              <TabsContent value="upper-primary" className="space-y-4">
                <div className="grid gap-8 md:grid-cols-2 items-center">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={activeTab === "upper-primary" ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-2xl font-bold mb-4">Upper Primary Program</h3>
                    <p className="text-muted-foreground mb-4">
                      Our Upper Primary program prepares students for secondary education with advanced concepts and
                      project-based learning. Students develop greater independence, critical thinking skills, and begin
                      to explore their individual interests and strengths.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start gap-2">
                        <BookOpen className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Complex Literacy:</span> Critical reading, analytical writing,
                          and research skills
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Advanced Mathematics:</span> Fractions, decimals, geometry, and
                          pre-algebraic concepts
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Palette className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Integrated Sciences:</span> Physical, life, and earth sciences
                          with hands-on experiments
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">Leadership Development:</span> Opportunities to develop
                          leadership skills through group projects and school activities
                        </span>
                      </li>
                    </ul>
                    <Button asChild>
                      <Link href="/online-admission">Apply for Upper Primary</Link>
                    </Button>
                  </motion.div>
                  <motion.div
                    className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
                    initial={{ opacity: 0, x: 50 }}
                    animate={activeTab === "upper-primary" ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Image
                      src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742039211/class7_p5sula.jpg"
                      alt="Upper primary classroom"
                      fill
                      className="object-cover"
                    />
                  </motion.div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* Curriculum Section */}
        <section id="curriculum" className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              className="mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">Our Curriculum</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our comprehensive curriculum is designed to develop well-rounded individuals with strong academic
                foundations, critical thinking skills, and cultural awareness.
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-bold mb-6">Core Subjects</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="language">
                    <AccordionTrigger>Language Arts</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our language arts curriculum develops strong communication skills in both English and Nepali.
                        Students learn reading, writing, speaking, and listening through engaging literature and
                        creative activities.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Reading comprehension and analysis</li>
                        <li>Creative and expository writing</li>
                        <li>Grammar and vocabulary development</li>
                        <li>Public speaking and presentation skills</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="mathematics">
                    <AccordionTrigger>Mathematics</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our mathematics program builds strong number sense and problem-solving skills through hands-on
                        activities and real-world applications.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Number operations and algebraic thinking</li>
                        <li>Geometry and spatial reasoning</li>
                        <li>Measurement and data analysis</li>
                        <li>Mathematical problem-solving strategies</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="science">
                    <AccordionTrigger>Science</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our science curriculum fosters curiosity and critical thinking through inquiry-based learning
                        and hands-on experiments.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Life sciences (plants, animals, human body)</li>
                        <li>Physical sciences (matter, energy, forces)</li>
                        <li>Earth and space sciences</li>
                        <li>Scientific method and investigation skills</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="social-studies">
                    <AccordionTrigger>Social Studies</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our social studies program helps students understand their place in the world, from local
                        community to global society, with emphasis on Nepali culture and history.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Nepali history, geography, and culture</li>
                        <li>World cultures and global awareness</li>
                        <li>Civic education and community involvement</li>
                        <li>Map skills and geographical concepts</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-bold mb-6">Enrichment Programs</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="arts">
                    <AccordionTrigger>Arts Education</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our arts program nurtures creativity and self-expression through various mediums and cultural
                        traditions.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Visual arts (drawing, painting, crafts)</li>
                        <li>Music (vocal and instrumental)</li>
                        <li>Dance (traditional Nepali and contemporary)</li>
                        <li>Drama and theatrical performances</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="physical-education">
                    <AccordionTrigger>Physical Education</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our physical education program promotes health, fitness, and teamwork through various sports and
                        activities.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Team sports (football, volleyball)</li>
                        <li>Individual sports (athletics, badminton)</li>
                        <li>Yoga and karate practices</li>
                        <li>Health and nutrition education</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="technology">
                    <AccordionTrigger>Technology Education</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our technology program develops digital literacy and responsible use of technology in the modern
                        world.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Basic computer skills and typing</li>
                        <li>Digital citizenship and online safety</li>
                        <li>Educational software and applications</li>
                        <li>Introduction to coding and computational thinking</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="life-skills">
                    <AccordionTrigger>Life Skills & Character Education</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground mb-2">
                        Our life skills program develops essential personal and interpersonal skills for success in
                        school and beyond.
                      </p>
                      <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                        <li>Social-emotional learning</li>
                        <li>Problem-solving and decision-making</li>
                        <li>Communication and conflict resolution</li>
                        <li>Values education (respect, responsibility, integrity)</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </motion.div>
            </div>
          </div>
        </section>

        

        {/* Extracurricular Activities Section */}
        <section className="py-8 md:py-12 bg-muted" ref={featuredRef}>
          <div className="container mx-auto px-4 ">
            <div className="flex items-center justify-center gap-3">
              <iframe width="560" height="315" src="https://www.youtube.com/embed/p2tzARPdF2g?si=gzbcpMYos9LgW0y9" title="YouTube video player" className="border-emerald-300 rounded-lg" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
              <iframe width="560" height="315" src="https://www.youtube.com/embed/mUjnA7vKvOA?si=UVWviwE94NW57euV" title="YouTube video player" className="border-emerald-300 rounded-lg" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
            </div>
          </div>
          <div className="container mx-auto px-4 mt-4">
            <motion.div
              className="mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">Extracurricular Activities</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We offer a wide range of extracurricular activities to help students discover and develop their talents
                and interests beyond the classroom.
              </p>
            </motion.div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  title: "Arts & Crafts Club",
                  description:
                    "Students explore various artistic mediums and techniques, from traditional Nepali arts to contemporary crafts.",
                  icon: <Palette className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Music & Dance",
                  description:
                    "Students learn traditional Nepali music and dance forms, as well as modern styles, with opportunities to perform at school events.",
                  icon: <Music className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Sports Teams",
                  description:
                    "Competitive and recreational sports including football, volleyball, and athletics, with inter-school tournaments.",
                  icon: <Trophy className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Yoga & Karate",
                  description:
                    "Students learn techniques for physical wellness, stress management, and mental focus through regular practice.",
                  icon: <Dumbbell className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Science Club",
                  description:
                    "Hands-on experiments, projects, and exploration of scientific concepts beyond the regular curriculum.",
                  icon: <BookOpen className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Reading Club",
                  description:
                    "Fostering a love of reading through book discussions, storytelling sessions, and literary activities.",
                  icon: <BookOpen className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Community Service",
                  description:
                    "Students engage in age-appropriate service projects to develop empathy and social responsibility.",
                  icon: <Users className="h-6 w-6 text-primary" />,
                },
                {
                  title: "Cultural Heritage Club",
                  description:
                    "Students learn about and celebrate Nepal's rich cultural heritage through activities, field trips, and special events.",
                  icon: <GraduationCapIcon className="h-6 w-6 text-primary" />,
                },
              ].map((activity, index) => (
                <motion.div
                  key={activity.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isFeaturedInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="mb-4 rounded-full bg-primary/10 p-3 w-12 h-12 flex items-center justify-center">
                        {activity.icon}
                      </div>
                      <h3 className="font-bold mb-2">{activity.title}</h3>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Academic Calendar Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              className="mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">Academic Calendar</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our academic year runs from April to March, aligned with the Nepali calendar, with breaks for major
                festivals and holidays.
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-2 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-bold mb-6">Key Dates</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Academic Year</h4>
                      <p className="text-muted-foreground">Baisakh to Chaitra</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">First Term</h4>
                      <p className="text-muted-foreground">Ashar</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Dashain/Tihar Break</h4>
                      <p className="text-muted-foreground">Ashoj/Kartik (2-3 weeks, varies by year)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Second Term</h4>
                      <p className="text-muted-foreground">Ashoj</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Winter Break</h4>
                      <p className="text-muted-foreground">Late Poush to early Magh (2 weeks)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Final Examinations</h4>
                      <p className="text-muted-foreground">Chaitra</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-bold mb-6">Daily Schedule</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">School Hours</h4>
                      <p className="text-muted-foreground">Sunday to Friday, 10:00 AM to 4:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Morning Assembly</h4>
                      <p className="text-muted-foreground">10:00 AM to 10:15 AM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Class Periods</h4>
                      <p className="text-muted-foreground">40 minutes each, with short breaks between periods</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Lunch Break</h4>
                      <p className="text-muted-foreground">1:00 PM to 1:45 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Extracurricular Activities</h4>
                      <p className="text-muted-foreground">2:50 PM to 3:30 PM (varies by day and activity)</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">Ready to Join Our School?</h2>
              <motion.p
                className="mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                Applications for the upcoming academic year are now open. Join our nurturing educational community where
                your child can thrive academically and personally.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <Button size="lg" variant="secondary" asChild>
                  <Link href="/online-admission">Apply for Admission</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-primary-foreground/10 hover:bg-primary-foreground/70"
                  asChild
                >
                  <Link href="/contact">Contact Us</Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
    </div>
  )
}

